import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, startCheckout, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIERS = [
  { id: "free", name: "Reader", desc: "For the curious and the connected.", features: ["Daily news briefs", "Selected free analysis", "The Tuesday Roast weekly newsletter", "One AI key takeaway per story"] },
  { id: "pro", name: "Pro", desc: "For trade advisors, buyers and founders.", popular: true, features: ["Everything in Reader", "All weekly analysis, unlocked", "Full AI key takeaways on every story", "Monthly Africa Brew Brief Report (PDF) included", "Sector deep-dives & data briefs", "Member access to the research store"] },
  { id: "institutional", name: "Institutional", desc: "For investors, institutions and teams.", features: ["Everything in Pro", "All research papers & sector reports included", "Institutional-only research papers", "Team access & invoiced billing", "Priority analyst briefings"] },
];

const PlanCard = ({ t, cycle, prices, current, onPick, busy }) => {
  const price = t.id === "free" ? 0 : prices[`${t.id}_${cycle}`]?.amount;
  const isCurrent = current === t.id;
  return (
    <div data-testid={`plan-card-${t.id}`} className={`relative flex flex-col p-8 border ${t.popular ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E7E7E7]"}`}>
      {t.popular && <span className="absolute -top-3 left-8 bg-[#E5332A] text-white overline !text-[9px] px-3 py-1">Most popular</span>}
      <p className={`overline ${t.popular ? "text-[#E5332A]" : "text-[#E5332A]"}`}>{t.name}</p>
      <p className={`text-sm mt-2 ${t.popular ? "text-[#E7E7E7]/70" : "text-[#575757]"}`}>{t.desc}</p>
      <div className="mt-8 flex items-baseline gap-2">
        <span className="font-display text-5xl font-bold" data-testid={`plan-price-${t.id}`}>{price === undefined ? "—" : gbp(price)}</span>
        {t.id !== "free" && <span className="text-sm opacity-70">/{cycle === "monthly" ? "month" : "year"}</span>}
      </div>
      {t.id !== "free" && cycle === "annual" && prices[`${t.id}_monthly`] && (
        <p className="text-xs mt-2 text-[#E5332A]">Save {gbp(prices[`${t.id}_monthly`].amount * 12 - price)} vs monthly</p>
      )}
      <ul className="mt-8 space-y-3 text-sm flex-1">
        {t.features.map((f) => <li key={f} className="flex gap-3"><Check size={16} className="text-[#E5332A] shrink-0 mt-0.5" /> {f}</li>)}
      </ul>
      <button data-testid={`subscribe-${t.id}-btn`} disabled={busy || isCurrent} onClick={() => onPick(t.id)}
        className={`mt-10 py-3 text-sm font-medium transition-colors disabled:opacity-60 ${t.popular ? "bg-[#E5332A] text-white hover:bg-[#FF4B40]" : "bg-[#0A0A0A] text-white hover:bg-[#E5332A]"}`}>
        {isCurrent ? "Your current plan" : t.id === "free" ? "Create free account" : busy === t.id ? "Opening checkout…" : `Subscribe ${cycle === "monthly" ? "monthly" : "annually"}`}
      </button>
    </div>
  );
};

export default function Pricing() {
  const [cycle, setCycle] = useState("annual");
  const [prices, setPrices] = useState({});
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { api.get("/plans").then((r) => setPrices(r.data.prices)); }, []);

  const pick = async (id) => {
    if (id === "free") return navigate(user ? "/news" : "/register");
    if (!user) return navigate("/register?next=/pricing");
    setBusy(id);
    try { await startCheckout(`${id}_${cycle}`); } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20" data-testid="pricing-page">
      <div className="grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-7">
          <p className="overline text-[#E5332A]">Membership</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05]">Intelligence that moves at the speed of the continent.</h1>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <div className="inline-flex border border-[#0A0A0A]" data-testid="pricing-billing-toggle">
            {["monthly", "annual"].map((c) => (
              <button key={c} data-testid={`billing-${c}-btn`} onClick={() => setCycle(c)} className={`px-6 py-2.5 text-sm capitalize transition-colors ${cycle === c ? "bg-[#0A0A0A] text-white" : "hover:bg-[#E7E7E7]"}`}>
                {c}{c === "annual" && <span className="ml-2 text-[10px] font-mono-d text-[#E5332A]">2 MONTHS FREE</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {TIERS.map((t) => <PlanCard key={t.id} t={t} cycle={cycle} prices={prices} current={user ? user.tier : null} onPick={pick} busy={busy} />)}
      </div>
      <p className="text-xs text-[#575757] mt-8">Prices in GBP. Cancel anytime from your account. Secure checkout by Stripe. Applicable taxes calculated at checkout.</p>
    </div>
  );
}
